import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminGiftCards() {
  const [cards, setCards] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ amount: "5000", recipient_name: "", recipient_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/gift-cards", { params: filter ? { status: filter } : {} });
      setCards(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const issue = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/gift-cards", {
        amount: Number(form.amount) || 0,
        recipient_name: form.recipient_name, recipient_email: form.recipient_email, message: form.message,
      });
      setIssued(r.data);
      toast.success(`Gift card issued: ${r.data.code}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-gift-cards">
      <div>
        <h1 className="font-heading text-2xl font-semibold">Gift Cards</h1>
        <p className="text-sm text-neutral-500 mt-1">Issue manual cards (refunds, goodwill) and track purchased ones. Customers buy from the storefront /gift-cards page; codes issue automatically on payment.</p>
      </div>

      <section className="border border-neutral-200 bg-white p-6" data-testid="gc-issue-section">
        <h2 className="font-heading font-semibold">Issue a card manually</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <div><Label>Amount (₹)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="gc-amount" /></div>
          <div><Label>Recipient name</Label><Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="gc-name" /></div>
          <div><Label>Recipient email</Label><Input value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} data-testid="gc-email" /></div>
          <div><Label>Message</Label><Input value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="gc-msg" /></div>
        </div>
        <Button onClick={issue} disabled={busy} className="mt-3 bg-neutral-900 text-white" data-testid="gc-issue-btn">{busy ? "Issuing…" : "Issue card"}</Button>
        {issued && <p className="mt-3 text-sm font-mono bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="gc-issued-code">Code: <strong>{issued.code}</strong> · {inr(issued.amount)}</p>}
      </section>

      <section className="border border-neutral-200 bg-white" data-testid="gc-list-section">
        <div className="flex items-center gap-2 p-4 border-b border-neutral-100">
          <span className="text-sm font-semibold">Cards</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 border border-neutral-200 px-2 text-xs bg-white" data-testid="gc-filter">
            <option value="">All</option><option value="active">Active</option><option value="redeemed">Redeemed</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-neutral-400 font-mono uppercase tracking-wider border-b border-neutral-100">
              <th className="p-3">Code</th><th className="p-3">Amount</th><th className="p-3">Balance</th><th className="p-3">Status</th><th className="p-3">Recipient</th><th className="p-3">Source</th>
            </tr></thead>
            <tbody>
              {cards.map((c) => (
                <tr key={c.id} className="border-t border-neutral-50" data-testid={`gc-row-${c.code}`}>
                  <td className="p-3 font-mono">{c.code}</td>
                  <td className="p-3">{inr(c.amount)}</td>
                  <td className="p-3">{inr(c.balance)}</td>
                  <td className="p-3"><span className={c.status === "active" ? "text-emerald-700" : "text-neutral-400"}>{c.status}</span></td>
                  <td className="p-3">{c.recipient_name || c.recipient_email || "—"}</td>
                  <td className="p-3 font-mono text-neutral-500">{c.order_number || "manual"}</td>
                </tr>
              ))}
              {!cards.length && <tr><td colSpan={6} className="p-6 text-neutral-400" data-testid="gc-empty">No gift cards yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
